/**
 * Job Documents API Contract
 *
 * Endpoints:
 *   GET  /jobs/documents/:jobId                → paginated list of job documents
 *   POST /jobs/documents/:jobId/upload-url     → presigned S3 POST for upload
 *   POST /jobs/documents/:jobId/complete       → persist metadata after S3 upload
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

/**
 * Zod schema for a single job document
 */
export const jobDocumentSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  mime_type: z.string().nullable(),
  type: z.string().nullable(),
  file_category: z.number().nullable(),
  s3_path: z.string().nullable(),
  created_by: z.string().nullable(),
  created_time: z.string().nullable(),
});

export const jobDocumentsResponseSchema = z.object({
  data: z.array(jobDocumentSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

export const jobDocumentsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});

/**
 * Body for requesting a presigned S3 upload URL.
 */
export const jobDocumentUploadUrlBodySchema = z.object({
  filename: z.string().min(1).max(255),
  content_type: z.string().min(1).max(200),
});

/**
 * Response shape returned by `createPresignedPost` from `@aws-sdk/s3-presigned-post`.
 * `fields` is a flat string→string map that the browser forwards as FormData alongside the file.
 * `object_key` is the final S3 key we'll persist after the upload completes.
 */
export const jobDocumentUploadUrlResponseSchema = z.object({
  url: z.string(),
  fields: z.record(z.string()),
  object_key: z.string(),
});

/**
 * Body for finalising a document upload. Called after the S3 PUT/POST succeeds.
 */
export const jobDocumentCompleteBodySchema = z.object({
  name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(200),
  object_key: z.string().min(1).max(1024),
  file_category: z.number().int().optional(),
});

export type JobDocument = z.infer<typeof jobDocumentSchema>;
export type JobDocumentsResponse = z.infer<typeof jobDocumentsResponseSchema>;
export type JobDocumentsQuery = z.infer<typeof jobDocumentsQuerySchema>;
export type JobDocumentUploadUrlBody = z.infer<typeof jobDocumentUploadUrlBodySchema>;
export type JobDocumentUploadUrlResponse = z.infer<typeof jobDocumentUploadUrlResponseSchema>;
export type JobDocumentCompleteBody = z.infer<typeof jobDocumentCompleteBodySchema>;

/**
 * Job Documents API Contract
 *
 * Base path: /jobs/documents  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const jobDocumentsContract = c.router({
  getJobDocuments: {
    method: 'GET',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    query: jobDocumentsQuerySchema,
    responses: {
      200: jobDocumentsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get documents for a job',
    description: 'Returns paginated documents associated with a specific job.',
  },

  getJobDocumentUploadUrl: {
    method: 'POST',
    path: '/:jobId/upload-url',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: jobDocumentUploadUrlBodySchema,
    responses: {
      200: jobDocumentUploadUrlResponseSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a presigned S3 upload URL for a job document',
    description:
      'Generates an S3 presigned POST that the client uses to upload a file directly to S3. ' +
      'The returned object_key must then be passed back via /complete to persist metadata.',
  },

  completeJobDocumentUpload: {
    method: 'POST',
    path: '/:jobId/complete',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: jobDocumentCompleteBodySchema,
    responses: {
      200: jobDocumentSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Persist a job document after S3 upload',
    description:
      'Creates a job_file row for a document that was just uploaded to S3 via the presigned URL.',
  },
});
